import { useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SCHOOLS } from '@crossmatch/shared';
import type { SchoolId } from '@crossmatch/shared';
import { candidatesApi } from '../api/candidates.js';
import type { CandidateCreateDto, CandidateDto, CandidateInput, CandidateVia } from '../api/types.js';
import { toUserMessage } from '../api/errors.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { EmptyState } from '../components/EmptyState.js';
import { ErrorState } from '../components/ErrorState.js';
import { Loading } from '../components/Loading.js';
import { Tag } from '../components/Tag.js';
import { formatHours, formatRelative, schoolLabel, skillLabel } from '../lib/format.js';
import { ONE_LINER_EXAMPLE, parseOneLiner } from '../lib/parse.js';
import { SLOT_OPTIONS } from '../lib/slots.js';
import { useCandidatesQuery } from '../hooks/queries.js';
import { toast } from '../store/toast.js';

const MAX_NAME_LENGTH = 24;

/** 录入结果（demo 改造②）：记录最近一次创建的实际解析路径，展示 AI 抽取徽标 */
interface LastCreated {
  name: string;
  via: CandidateVia;
}

function CandidateRow({ candidate, onDelete }: { candidate: CandidateDto; onDelete: (item: CandidateDto) => void }) {
  const slots = candidate.slots ?? [];
  return (
    <article className="candidate-card">
      <div className="row">
        <strong className="grow">{candidate.name}</strong>
        <Tag tone="school" title={`学校 id：${candidate.school}`}>
          {schoolLabel(candidate.school)}
        </Tag>
        {candidate.major ? <Tag>{candidate.major}</Tag> : null}
        <Tag tone="primary">{formatHours(candidate.weeklyHours)}</Tag>
        <Button variant="danger" size="sm" onClick={() => onDelete(candidate)}>
          删除
        </Button>
      </div>
      <div className="row-tight">
        {candidate.skills.length === 0 ? (
          <Tag tone="warn">没有已收录技能：匹配时「需求覆盖」会是 0</Tag>
        ) : (
          candidate.skills.map((skillId) => (
            <Tag key={skillId} tone="primary">
              {skillLabel(skillId)}
            </Tag>
          ))
        )}
      </div>
      <div className="row-tight">
        <span className="muted small">时段：{slots.length > 0 ? slots.join('、') : '未填写'}</span>
        <span className="muted small">
          userId：<code className="mono">{candidate.ownerId ?? candidate.userId}</code>
        </span>
        {candidate.createdAt ? <span className="muted small">录入于 {formatRelative(candidate.createdAt)}</span> : null}
      </div>
    </article>
  );
}

/** 候选人池内容面板（不含 page-head）：独立拆出，供「候选人池」页和「开始匹配 → 候选人池 Tab」复用。 */
export function CandidatesPanel() {
  const [eventCodeInput, setEventCodeInput] = useState('');
  const [appliedCode, setAppliedCode] = useState('');
  const [name, setName] = useState('');
  const [oneLiner, setOneLiner] = useState('');
  const [schoolOverride, setSchoolOverride] = useState<SchoolId | ''>('');
  const [hoursText, setHoursText] = useState('');
  const [hoursTouched, setHoursTouched] = useState(false);
  const [slotsOverride, setSlotsOverride] = useState<string[] | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CandidateDto | null>(null);
  const [lastCreated, setLastCreated] = useState<LastCreated | null>(null);

  const nameRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const candidatesQuery = useCandidatesQuery(appliedCode.length > 0 ? appliedCode : undefined);
  const parsed = useMemo(() => parseOneLiner(oneLiner), [oneLiner]);

  const effectiveSchool: SchoolId | '' = schoolOverride !== '' ? schoolOverride : parsed.school ?? '';
  const effectiveSlots = slotsOverride ?? parsed.slots;
  const parsedHours = parsed.weeklyHours !== null ? String(parsed.weeklyHours) : '';
  const hoursValue = hoursTouched ? hoursText : parsedHours;

  const resetForm = (): void => {
    setName('');
    setOneLiner('');
    setSchoolOverride('');
    setHoursText('');
    setHoursTouched(false);
    setSlotsOverride(null);
    setFormError(null);
  };

  const create = useMutation<CandidateCreateDto, Error, CandidateInput>({
    mutationFn: (input: CandidateInput) => candidatesApi.create(input),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['candidates'] });
      setLastCreated({ name: data.candidate.name, via: data.via });
      if (data.via === 'rule') {
        toast.info('已用规则解析（智能解析不可用）', '未配置 LLM 或智能解析失败时，系统会用内置规则解析，功能不受影响');
      } else if (data.via === 'llm') {
        toast.success('AI 智能抽取完成', '本条候选人的字段由大模型从原话中抽取并归一到词表');
      }
      const unmatched = parsed.unmatched;
      if (unmatched.length > 0) {
        toast.info(
          `已录入候选人${name.trim().length > 0 ? `：${name.trim()}` : ''}`,
          `${unmatched.length} 个词未收录，已标为「待归类」且未计入技能：${unmatched.join('、')}`,
        );
      } else if (data.via === 'manual') {
        toast.success('已录入候选人', '可以继续录入，或去「开始匹配」跑一次');
      }
      resetForm();
    },
    onError: (error) => {
      setFormError(`新增候选人失败：${toUserMessage(error)}`);
    },
  });

  const remove = useMutation({
    mutationFn: (candidateId: string) => candidatesApi.remove(candidateId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['candidates'] });
      toast.success('已删除候选人', '该候选人不会再出现在匹配池里');
      setPendingDelete(null);
    },
    onError: () => {
      // 失败提示交给 App.tsx 的全局 MutationCache.onError 统一发出，
      // 这里不再单独 toast，避免同一次失败弹两次。
      setPendingDelete(null);
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setFormError('新增候选人失败：请填写候选人姓名（上限 24 字）');
      nameRef.current?.focus();
      return;
    }
    if (trimmedName.length > MAX_NAME_LENGTH) {
      setFormError(`新增候选人失败：姓名过长（当前 ${trimmedName.length} 字，上限 ${MAX_NAME_LENGTH} 字）`);
      return;
    }
    if (effectiveSchool === '') {
      setFormError('新增候选人失败：没有识别到学校，请在「解析预览」里手动选择学校');
      return;
    }
    const hours = hoursValue.trim().length === 0 ? 10 : Number(hoursValue);
    if (!Number.isFinite(hours) || hours < 0 || hours > 60) {
      setFormError('新增候选人失败：每周可投入时间需在 0–60 小时之间');
      return;
    }

    const input: CandidateInput = {
      name: trimmedName,
      school: effectiveSchool,
      skills: parsed.skills, // 只提交已收录技能；待归类词绝不进来
      weeklyHours: hours,
      slots: effectiveSlots,
    };
    if (appliedCode.length > 0) input.eventCode = appliedCode;
    // 一句话原文一并提交：后端 LLM 优先/规则兜底抽取补缺（响应 via 标注实际路径）
    if (oneLiner.trim().length > 0) input.oneLiner = oneLiner.trim();
    setFormError(null);
    create.mutate(input);
  };

  return (
    <>
      <Card title="一句话录入" subtitle="把同学的原话粘进来，学校 / 技能 / 时长自动解析；提交时智能抽取补缺（无 LLM 时自动回退规则）。">
        <form className="stack" onSubmit={handleSubmit} noValidate>
          {formError !== null ? (
            <div className="banner banner-danger" role="alert">
              <span className="banner-ico" aria-hidden="true">
                !
              </span>
              <span className="wrap-anywhere">{formError}</span>
            </div>
          ) : null}

          <div className="grid-2">
            <div className="field">
              <label className="field-label" htmlFor="candidate-name">
                姓名 / 昵称（必填，≤24 字）
              </label>
              <input
                id="candidate-name"
                ref={nameRef}
                className="input"
                value={name}
                maxLength={MAX_NAME_LENGTH}
                placeholder="如：小林"
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor="candidate-event-code">
                赛事码（选填，用于池子隔离）
              </label>
              <div className="inline-form">
                <div className="field">
                  <input
                    id="candidate-event-code"
                    className="input"
                    value={eventCodeInput}
                    placeholder="如 EV2026（6–12 位大写字母数字）"
                    onChange={(event) => setEventCodeInput(event.target.value.toUpperCase())}
                  />
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setAppliedCode(eventCodeInput.trim().toUpperCase())}
                >
                  按赛事码筛选
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEventCodeInput('');
                    setAppliedCode('');
                  }}
                >
                  清除
                </Button>
              </div>
              <span className="field-hint">
                {appliedCode.length > 0
                  ? `当前筛选：${appliedCode}（后端规则：event_code = ${appliedCode} 或为空的人都会返回）`
                  : '不填则查看全部候选人。'}
              </span>
            </div>
          </div>

          <div className="field">
            <label className="field-label" htmlFor="candidate-one-liner">
              一句话描述
            </label>
            <textarea
              id="candidate-one-liner"
              className="textarea"
              value={oneLiner}
              placeholder={`例如：${ONE_LINER_EXAMPLE}`}
              onChange={(event) => setOneLiner(event.target.value)}
            />
            <span className="field-hint">
              解析顺序：先识别学校（detectSchool）与时长，再用词表最长匹配 + normalizeSkill 认技能，剩下看不懂的词进「待归类」。
            </span>
          </div>

          <div className="parse-preview">
            <div className="parse-row">
              <span className="parse-label">学校</span>
              {parsed.school !== null ? (
                <>
                  <Tag tone="school">{schoolLabel(parsed.school)}</Tag>
                  <span className="muted small">识别关键词「{parsed.schoolHint}」</span>
                </>
              ) : (
                <span className="muted small">未识别 —— 请在右侧手动选择（不选无法提交）</span>
              )}
              <select
                className="select"
                aria-label="候选人的学校"
                value={effectiveSchool}
                onChange={(event) => setSchoolOverride(event.target.value as SchoolId | '')}
                style={{ maxWidth: '220px' }}
              >
                <option value="">请选择学校</option>
                {SCHOOLS.map((school) => (
                  <option key={school.id} value={school.id}>
                    {school.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="parse-row">
              <span className="parse-label">已收录技能</span>
              {parsed.skills.length > 0 ? (
                parsed.skills.map((skillId) => (
                  <Tag key={skillId} tone="primary">
                    {skillLabel(skillId)}
                  </Tag>
                ))
              ) : (
                <span className="muted small">未识别到已收录技能（提交后该候选人「需求覆盖」会是 0）</span>
              )}
            </div>

            <div className="parse-row">
              <span className="parse-label">待归类</span>
              {parsed.unmatched.length > 0 ? (
                <>
                  {parsed.unmatched.map((word) => (
                    <Tag key={word} tone="warn" title="未收录词：不计入技能，也不会参与匹配计分">
                      {word}
                    </Tag>
                  ))}
                  <span className="muted small">
                    未收录 ≠ 不会：这些词不会被偷偷丢掉，但也不会被当成技能计分。需要收录请让管理员补 shared/SKILLS 词表。
                  </span>
                </>
              ) : (
                <span className="muted small">没有待归类词</span>
              )}
            </div>

            <div className="parse-row">
              <span className="parse-label">每周可投入</span>
              <input
                className="input"
                type="number"
                min={0}
                max={60}
                step={1}
                aria-label="候选人每周可投入小时数"
                style={{ maxWidth: '140px' }}
                value={hoursValue}
                onChange={(event) => {
                  setHoursTouched(true);
                  setHoursText(event.target.value);
                }}
              />
              <span className="muted small">小时/周（0–60；解析不到时默认 10）</span>
            </div>

            <div className="parse-row">
              <span className="parse-label">可协作时段</span>
              {SLOT_OPTIONS.map((slot) => (
                <button
                  key={slot}
                  type="button"
                  className={effectiveSlots.includes(slot) ? 'chip on' : 'chip'}
                  aria-pressed={effectiveSlots.includes(slot)}
                  onClick={() => {
                    setSlotsOverride(
                      effectiveSlots.includes(slot)
                        ? effectiveSlots.filter((item) => item !== slot)
                        : [...effectiveSlots, slot],
                    );
                  }}
                >
                  {slot}
                </button>
              ))}
            </div>
          </div>

          <div className="row">
            <Button type="submit" variant="primary" loading={create.isPending}>
              {create.isPending ? '正在录入…' : '录入候选人'}
            </Button>
            <Button variant="ghost" onClick={resetForm} disabled={create.isPending}>
              清空表单
            </Button>
          </div>

          {create.isPending && oneLiner.trim().length > 0 ? (
            <div className="extract-flow" role="status" aria-label="AI 正在解析">
              <span className="extract-flow-title">✨ AI 解析中</span>
              <ol className="extract-flow-steps">
                <li>大模型抽取</li>
                <li>技能词表归一</li>
                <li>写入候选池</li>
              </ol>
            </div>
          ) : null}

          {!create.isPending && lastCreated !== null && lastCreated.via !== 'manual' ? (
            <div className="extract-result">
              <span className="muted small">上次录入「{lastCreated.name}」：</span>
              {lastCreated.via === 'llm' ? (
                <Tag tone="primary" title="本条由后端真实调用大模型抽取">
                  ✓ AI 抽取（LLM）
                </Tag>
              ) : (
                <Tag tone="muted" title="未配置 LLM Key 或调用失败，已自动回退内置规则解析">
                  规则解析（未走 LLM）
                </Tag>
              )}
            </div>
          ) : null}
        </form>
      </Card>

      <Card
        title="候选人列表"
        subtitle={
          candidatesQuery.data
            ? `共 ${candidatesQuery.data.candidates.length} 人${appliedCode.length > 0 ? `（筛选：${appliedCode}）` : ''}`
            : '正在读取…'
        }
      >
        {candidatesQuery.isPending ? (
          <Loading label="正在读取候选人…" rows={3} />
        ) : candidatesQuery.isError ? (
          <ErrorState error={candidatesQuery.error} title="读取候选人失败" onRetry={() => void candidatesQuery.refetch()} />
        ) : candidatesQuery.data.candidates.length === 0 ? (
          <EmptyState
            icon="＋"
            title="候选人池还是空的"
            description="先录入至少 1 位候选人，匹配页才有可以排序的对象。最快的办法是把同学的原话粘到上面的「一句话描述」里。"
            action={
              <Button variant="primary" onClick={() => nameRef.current?.focus()}>
                去录入第一位候选人
              </Button>
            }
          />
        ) : (
          <>
            <p className="field-hint">
              邀请入队：把对方的邀请 ID（CM-XXXXXX）填到「我的队伍 → 按 ID 查找」，或直接用上面的 userId。
            </p>
            <div className="candidate-list">
              {candidatesQuery.data.candidates.map((candidate) => (
                <CandidateRow key={candidate.id} candidate={candidate} onDelete={setPendingDelete} />
              ))}
            </div>
          </>
        )}
      </Card>

      <ConfirmDialog
        open={pendingDelete !== null}
        danger
        title="删除这位候选人？"
        description={
          pendingDelete ? (
            <>
              <p>
                将删除「{pendingDelete.name}」（{schoolLabel(pendingDelete.school)}）。删除后他/她不会再出现在匹配池里，历史匹配记录不受影响。
              </p>
              <p className="muted small">此操作不可撤销。</p>
            </>
          ) : null
        }
        confirmText="删除候选人"
        pending={remove.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) remove.mutate(pendingDelete.id);
        }}
      />
    </>
  );
}

/** 候选人池独立页：page-head + 复用的内容面板（同一块内容也嵌在「开始匹配 → 候选人池 Tab」）。 */
export function CandidatesPage() {
  const candidatesQuery = useCandidatesQuery();
  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-title">候选人池</h1>
          <p className="page-sub">
            录入只认词表技能；<strong>未收录的词显示为「待归类」，不计入技能、不参与匹配计分</strong>。
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => void candidatesQuery.refetch()}>
          刷新列表
        </Button>
      </div>
      <CandidatesPanel />
    </div>
  );
}